/**
 * Deal detail — the target of every card on the pipeline board.
 *
 * Status lives here rather than only on the board because moving to a lost stage
 * needs a reason in the same request; the board redirects here for exactly that.
 */

import { useFetcher, useLoaderData, useSearchParams } from 'react-router-dom'
import type { ActionFunctionArgs, LoaderFunctionArgs } from 'react-router-dom'
import type { Deal } from '@erp/contracts'
import type { Contact, Organization } from '@erp/contracts'
import type { Assignment, Note, Task, TimelineResult } from '@erp/contracts'
import { api, ApiError } from '../api'
import { handleSubjectIntent } from '../detail-actions'
import { Detail, RecordDetail } from '../components/record-detail'
import { AssigneePicker } from '../components/assignee-picker'
import { Badge, Button, Field, PageHeader, Select } from '../ui'
import { useDealStatus, useDealStatuses, useLostReasons , useUser } from '../status-context'
import { contactName, formatDate, formatMoney } from '../format'

interface Data {
  deal: Deal
  organization: Organization | null
  contact: Contact | null
  timeline: TimelineResult
  notes: Note[]
  tasks: Task[]
  assignments: Assignment[]
}

export async function loader({ params }: LoaderFunctionArgs): Promise<Data> {
  const id = params.dealId
  if (!id) throw new Response('Missing deal id', { status: 400 })

  const [deal, timeline, notes, tasks, assignments] = await Promise.all([
    api.deals.get(id),
    api.deals.timeline(id, { limit: 50 }),
    api.notes.deletedFor('deal', id),
    api.tasks.forSubject('deal', id),
    api.assignments.forSubject('deal', id),
  ])

  const [organization, contact] = await Promise.all([
    deal.organizationId ? api.organizations.get(deal.organizationId) : Promise.resolve(null),
    deal.primaryContactId ? api.contacts.get(deal.primaryContactId) : Promise.resolve(null),
  ])

  return { deal, organization, contact, timeline, notes: notes.rows, tasks: tasks.rows, assignments }
}

export async function action({ request, params }: ActionFunctionArgs) {
  const id = params.dealId
  if (!id) throw new Response('Missing deal id', { status: 400 })

  const form = await request.formData()
  const intent = String(form.get('intent'))

  const shared = await handleSubjectIntent(intent, form)
  if (shared) return shared

  try {
    if (intent === 'status') {
      const reason = form.get('lostReasonId')
      await api.deals.changeStatus(id, {
        statusId: String(form.get('statusId')),
        lostReasonId: typeof reason === 'string' && reason.length > 0 ? reason : null,
      })
      return { ok: true }
    }
    return { ok: false, message: 'Unknown action' }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message, issues: error.issues }
    throw error
  }
}

export default function DealDetail() {
  const { deal, organization, contact, timeline, notes, tasks, assignments } = useLoaderData() as Data
  const status = useDealStatus(deal.statusId)
  const owner = useUser(deal.ownerId)
  const [searchParams] = useSearchParams()

  // The board sends ?intent=lost when a card is dropped on a losing stage,
  // because it cannot collect a reason inline.
  const promptForLoss = searchParams.get('intent') === 'lost'

  return (
    <>
      <PageHeader
        title={organization?.name ?? 'Deal'}
        actions={
          <>
            <Badge color={status?.color ?? 'gray'}>{status?.name ?? '—'}</Badge>
            <a className="ax-btn" href={`/crm/deals/${deal.id}/edit`}>
              Edit
            </a>
          </>
        }
      />

      <RecordDetail
        subject={{ type: 'deal', id: deal.id }}
        timeline={timeline}
        notes={notes}
        tasks={tasks}
        side={
          <>
            <AssigneePicker subject={{ type: 'deal', id: deal.id }} assignments={assignments} />
            <StatusPicker deal={deal} promptForLoss={promptForLoss} />
            <Detail label="Owner" value={owner?.name ?? null} />
            <Detail
              label="Amount"
              value={deal.amount !== null ? formatMoney(deal.amount, deal.currency) : null}
            />
            <Detail label="Probability" value={deal.probability !== null ? `${deal.probability}%` : null} />
            <Detail label="Expected close" value={deal.expectedCloseDate} />
            <Detail label="Primary contact" value={contact ? contactName(contact) : null} />
            <Detail label="Won" value={deal.wonAt ? formatDate(deal.wonAt) : null} />
            <Detail label="Lost" value={deal.lostAt ? formatDate(deal.lostAt) : null} />
          </>
        }
      />
    </>
  )
}

function StatusPicker({ deal, promptForLoss }: { deal: Deal; promptForLoss: boolean }) {
  const statuses = useDealStatuses()
  const lostReasons = useLostReasons()
  const fetcher = useFetcher()

  const pending = fetcher.formData?.get('statusId')
  const selectedId = typeof pending === 'string' ? pending : deal.statusId
  const selected = statuses.find((s) => s.id === selectedId)

  // A losing stage needs a reason in the same request, so the form only submits
  // once one is chosen.
  const needsReason = selected?.outcome === 'lost' || promptForLoss

  return (
    <fetcher.Form method="post">
      <input type="hidden" name="intent" value="status" />
      <Field label="Stage">
        <Select
          name="statusId"
          value={selectedId}
          disabled={fetcher.state !== 'idle'}
          options={statuses.map((s) => ({ value: s.id, label: s.name }))}
          onChange={(next) => {
            const target = statuses.find((s) => s.id === next)
            // Losing stages wait for the reason; everything else submits straight away.
            if (target?.outcome !== 'lost') {
              fetcher.submit({ intent: 'status', statusId: next }, { method: 'post' })
            }
          }}
        />
      </Field>

      {needsReason ? (
        <>
          <Field label="Lost reason">
            <Select
              name="lostReasonId"
              value={deal.lostReasonId ?? ''}
              options={[
                { value: '', label: 'Select a reason…' },
                ...lostReasons.map((reason) => ({ value: reason.id, label: reason.name })),
              ]}
            />
          </Field>
          <Button variant="danger" type="submit">
            Mark lost
          </Button>
        </>
      ) : null}
    </fetcher.Form>
  )
}



